/* eslint-disable no-underscore-dangle */
/* eslint-disable class-methods-use-this */

import { isTestMode } from '../globals';
import { buildApiRequestContext } from '../helpers/context';
import { ActivateLiveChatContentType, ChatMessage, InputTypes } from '../store';

export interface LiveChatResponse {
	responses: ChatMessage[];
	success: boolean;
	reason?: string;
}

export interface LiveChatPollRequest {
	participantId: string;
	sessionId: string;
	keepAlive: boolean;
}

export interface LiveChatSendMessageRequest {
	participantId: string;
	sessionId: string;
	message: string;
	inputType: InputTypes;
}

export interface LiveChatSetTypingRequest {
	participantId: string;
	sessionId: string;
	typingIndicator: boolean;
}

export interface LiveChatExitRequest {
	sessionId: string;
	participantId: string;
}

export class LiveChatService {
	isReadOnly = false; // TODO: Should be a private property, but jest doesn't like it

	constructor() {
		this.isReadOnly = false;
	}

	async start(
		customInfo: ActivateLiveChatContentType
	): Promise<LiveChatResponse> {
		if (this.isReadOnly) {
			return Promise.resolve({ success: true, responses: [] });
		}

		if (window.chatbotDebug && window.chatbotDebug.breakLiveChatStart) {
			return Promise.reject();
		}

		try {
			const response = await fetch(`${CHAT_API_URL}/livechat/start`, {
				method: 'POST',
				headers: {
					'x-api-key': CHAT_API_KEY,
				},
				body: JSON.stringify({
					...customInfo,
					isTestMode,
					requestContext: buildApiRequestContext(),
				}),
			});

			if (!response.ok) {
				return Promise.reject(response.statusText);
			}

			const responseData: LiveChatResponse = await response.json();

			return Promise.resolve(responseData);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async poll(request: LiveChatPollRequest): Promise<LiveChatResponse> {
		if (this.isReadOnly) {
			return Promise.resolve({ success: true, responses: [] });
		}

		if (window.chatbotDebug && window.chatbotDebug.breakLiveChatPoll) {
			return Promise.reject(new Error('breakLiveChatPoll'));
		}

		try {
			const query = new URLSearchParams();
			query.append('participantId', request.participantId);
			query.append('sessionId', request.sessionId);
			query.append('keepAlive', request.keepAlive.toString());
			query.append('isTestMode', isTestMode.toString());
			query.append('requestContext', JSON.stringify(buildApiRequestContext()));

			const response = await fetch(
				`${CHAT_API_URL}/livechat/poll?${query.toString()}`,
				{
					method: 'GET',
					headers: {
						'x-api-key': CHAT_API_KEY,
					},
				}
			);

			if (!response.ok) {
				return Promise.reject(response.statusText);
			}

			const responseData: LiveChatResponse = await response.json();

			return Promise.resolve(responseData);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async sendMessage(
		request: LiveChatSendMessageRequest
	): Promise<LiveChatResponse> {
		if (this.isReadOnly) {
			return Promise.resolve({ success: true, responses: [] });
		}

		try {
			const response = await fetch(`${CHAT_API_URL}/livechat/sendmessage`, {
				method: 'POST',
				headers: {
					'x-api-key': CHAT_API_KEY,
				},
				body: JSON.stringify({
					...request,
					isTestMode,
					requestContext: buildApiRequestContext(),
				}),
			});

			if (!response.ok) {
				return Promise.reject(response.statusText);
			}

			const responseData: LiveChatResponse = await response.json();

			return Promise.resolve(responseData);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async setTypingState(request: LiveChatSetTypingRequest): Promise<void> {
		if (this.isReadOnly) {
			return Promise.resolve();
		}

		try {
			const response = await fetch(`${CHAT_API_URL}/livechat/settypingstate`, {
				method: 'POST',
				headers: {
					'x-api-key': CHAT_API_KEY,
				},
				body: JSON.stringify({ ...request, isTestMode }),
			});

			if (!response.ok) {
				return Promise.reject(response.statusText);
			}

			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async exit(request: LiveChatExitRequest): Promise<void> {
		if (this.isReadOnly) {
			return Promise.resolve();
		}

		try {
			const response = await fetch(`${CHAT_API_URL}/livechat/exit`, {
				method: 'POST',
				headers: {
					'x-api-key': CHAT_API_KEY,
				},
				body: JSON.stringify({
					...request,
					isTestMode,
					requestContext: buildApiRequestContext(),
				}),
			});

			if (!response.ok) {
				return Promise.reject(response.statusText);
			}

			return Promise.resolve();
		} catch (error) {
			return Promise.reject();
		}
	}
}

export default LiveChatService;
